import { ProfileLearningService } from './profile-learning.service';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { ProfileOverviewActionsServiceV2 } from '@app/profile/components/profile-overview-v2/profile-overview-actions/profile-overview-actions.service';
import { ProfileOverviewServiceV2 } from '@app/profile/components/profile-overview-v2/profile-overview.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { TagsApi } from '@app/tags/tag-api';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { InputsService } from '@app/inputs/services/inputs.service';
import { Profile } from '@app/profile/profile';
import {
  CollectionItemType,
  ProfileCollectionItem,
  ProfileCollectionModel,
} from '@app/profile/profile-api';
import { SearchFacet } from '@app/search/search-api';
import { InputType } from '@app/shared/models/core-api';
import { isOutcomeTile } from '@app/profile/components/profile-collection/services/profile-collection-helpers';
import { InputDetails } from '@app/inputs/inputs-api';
import { InputModalDispatcherService } from '@app/user-content/user-input/services/input-modal-dispatcher.service';
import { ContentCardService } from '@app/shared/components/content/content-card/content-card.service';
import { Params } from '@angular/router';
import { getEmptyTileCount } from '../profile-overview-helpers';

@Component({
  selector: 'dgx-profile-learning-v2',
  templateUrl: './profile-learning.component.html',
  styleUrls: ['./profile-learning.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProfileLearningComponentV2
  extends SubscriberBaseDirective
  implements OnInit, OnChanges, OnDestroy
{
  @Input() public profile: Profile;
  @Input() public ownerIsViewing: boolean;
  @Input() public learningDisplayLimit: number = 3;
  @Input() public showViewAll: boolean = true;
  @Input() public showAdd: boolean = true;
  @Input() public useLoadingTemplate: boolean;
  @Input() public useShowMore: boolean;

  @Input() public useCarousel: boolean = true;
  @Input() public tag: TagsApi.Tag;
  // This can be used broadly to show all learnings, or learnings for a specific tag
  // if a tag is required, make sure to wait for the tag to be available before fetching learnings
  @Input() public requiresTag: boolean;

  @Input() public isPageLoading: boolean;
  @Input() public pageType: string;
  @Input() public isMobile: boolean = false;
  @Input() public cardRowLimit: number = 3; // only in use when useCarousel is false

  @Output() public learningsLoading = new EventEmitter<{
    type: string;
    status: boolean;
  }>();

  public learnings: ProfileCollectionItem<InputDetails>[] = [];
  public hasMoreItems: boolean;
  public facets: SearchFacet[];
  public viewAllUrl: string;
  public titleLimit = 50;
  public isEmpty: boolean;
  public emptyTiles: number[];

  public currentStartIndex: number = 0;
  public currentEndIndex: number;
  public sectionLoading: boolean = true;
  public viewAllUrlQueryParams: Params;
  public indexesOfExpandedCards$: Observable<number[]>;

  public i18n = this.translateService.instant([
    'Core_Add',
    'dgProfileOverview_AddLearning',
    'Core_Article',
    'Core_ArticlesDisplayName',
    'Core_Podcast',
    'Core_PodcastsDisplayName',
    'Core_Video',
    'Core_VideosDisplayName',
    'Core_Book',
    'Core_BooksDisplayName',
    'Core_Course',
    'Core_CoursesDisplayName',
    'Core_Event',
    'Core_EventsDisplayName',
    'Core_Assessment',
    'Core_AssessmentsDisplayName',
    'UserController_Type',
    'dgProfileOverview_LearningTitle',
    'Core_AllTypesDisplayName',
    'Core_TasksDisplayName',
    'Core_PostsDisplayName',
    'Core_Provider',
    'Core_ShowMore',
    'UserInputsSvc_AddToProfileError',
  ]);

  public menuConfig: MenuViewModel[] = [
    {
      title: this.i18n.Core_Article,
      defaultAction: () => this.showAddModal('Article'),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Podcast,
      defaultAction: () => this.showAddModal('Episode'),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Video,
      defaultAction: () => this.showAddModal('Video'),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Book,
      defaultAction: () => this.showAddModal('Book'),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Course,
      defaultAction: () => this.showAddModal('Course'),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Event,
      defaultAction: () => this.showAddModal('Event'),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Assessment,
      defaultAction: () => this.showAddModal('Assessment'),
      preventRefocus: true,
    },
  ];

  constructor(
    private translateService: TranslateService,
    private inputsService: InputsService,
    private profileOverviewService: ProfileOverviewServiceV2,
    private profileOverviewActionsService: ProfileOverviewActionsServiceV2,
    private genericModalService: InputModalDispatcherService,
    private contentCardService: ContentCardService,
    private learningService: ProfileLearningService
  ) {
    super();

    this.inputsService.inputModify
      .pipe(this.takeUntilDestroyed())
      .subscribe((contentType) => {
        if (contentType !== 'Position' && !isOutcomeTile(contentType)) {
          this.updateLearnings(true);
        }
      });
  }

  public modifyOptionsFn = (content, defaultConfig) => {
    return this.profileOverviewActionsService.modifyInputMenuOptions(
      this.ownerIsViewing,
      false,
      content,
      defaultConfig
    );
  };

  public showAddModal(outcomeType: InputType): void {
    this.genericModalService
      .complete({
        inputType: outcomeType,
      })
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.inputsService.notifyInputModified(outcomeType);
      });
  }

  public ngOnInit(): void {
    const ownerUrl = `/${this.profile.vanityUrl}`;
    this.viewAllUrl = `/profile${ownerUrl}/collection`;
    this.viewAllUrlQueryParams = {
          classifier: 'learning',
        }
    this.getInitialLearnings();
    this.indexesOfExpandedCards$ =
      this.contentCardService.getIndexesOfExpandedCards();
  }

  public ngOnDestroy(): void {
    this.contentCardService.setIndexesOfExpandedCards([]);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.pageType || changes?.tag) {
      this.getInitialLearnings();
    }
  }

  public getInitialLearnings(): void {
    if (this.requiresTag && !this.tag) {
      return;
    }
    this.setLoadingState(true);

    this.learningService
      .getMostRecentLearningsModel(
        this.profile.id,
        0,
        this.learningDisplayLimit,
        this.tag?.name,
        '',
        []
      )
      .subscribe((learningsModel: ProfileCollectionModel<InputDetails>) => {
        this.learnings = this.extractLearningItemsFromModel(learningsModel);
        this.facets = learningsModel.facets;
        this.hasMoreItems = this.getHasMoreItems(learningsModel.hasMoreItems);
        this.setup();
      });
  }

  public setup(): void {
    this.isEmpty = !this.learnings.length;
    this.setEmptyTiles();

    this.currentEndIndex = this.learnings.length - 1;
    this.setLoadingState(false);
    this.sectionLoading = false;
    this.resetSettings();
  }

  public loadMoreLearnings(index: number): void {
    if (index === this.currentEndIndex && this.hasMoreItems) {
      this.sectionLoading = true;
      this.currentStartIndex = this.currentEndIndex + 1;
      this.currentEndIndex = this.currentStartIndex + this.learningDisplayLimit;
      this.updateLearnings();
    }
  }

  public trackByConfigId(index: number, section: InputDetails): number {
    return section.inputId;
  }

  public trackByTypeName(index: number, itemType: CollectionItemType): string {
    return itemType.name;
  }

  public trackByCollectionId(
    index: number,
    achievement: ProfileCollectionItem<InputDetails>
  ) {
    return achievement.itemId;
  }

  public getHasMoreItems(returnedHasMoreItems): boolean {
    // This is a temporary workaround for a BE bug where hasMoreItems == true if the totalCount is
    // greater than OR EQUAL to the number of items returned, resulting in the showMore / scroll for more
    // being enabled when there are no more items to fetch
    const totalCount =
      this.facets.find((f) => f.id === 'Category')?.values[0]?.count || 0;
    return returnedHasMoreItems && totalCount > this.learnings.length;
  }

  private setLoadingState(status): void {
    this.learningsLoading.emit({ type: 'learnings', status });
  }

  private resetSettings(): void {
    this.isEmpty = !this.learnings.length;
    this.setEmptyTiles();
  }

  private setEmptyTiles(): void {
    if (!this.isMobile) {
      this.emptyTiles = getEmptyTileCount(this.learnings.length, this.cardRowLimit);
    }
  }

  private extractLearningItemsFromModel(
    model: ProfileCollectionModel<InputDetails>
  ): ProfileCollectionItem<InputDetails>[] {
    return model.items.reduce((allItems, item) => {
      allItems.push(item.content);
      return allItems;
    }, []);
  }

  private updateLearnings(replaceLearnings = false): void {
    const ownerKey = this.profile.id;
    if (replaceLearnings) {
      // getting a new set of learnings due to adding / deleting / filtering so set the start index to 0
      this.currentStartIndex = 0;
    }
    this.learningService
      .getMostRecentLearningsModel(
        ownerKey,
        this.currentStartIndex,
        this.learningDisplayLimit,
        this.tag?.name,
        '',
        []
      )
      .subscribe((learningsModel: ProfileCollectionModel<InputDetails>) => {
        const newLearnings = this.extractLearningItemsFromModel(learningsModel);
        if (this.currentStartIndex === 0 || replaceLearnings) {
          this.learnings = newLearnings;
        } else {
          this.learnings.push(...newLearnings);
        }
        this.facets = learningsModel.facets;
        this.hasMoreItems = this.getHasMoreItems(learningsModel.hasMoreItems);
        this.setup();
      });
  }
}
